// FE-SCRM-049 — Filter bar above the prospects table.
//
// All controls write straight to the URL via the filterState codec
// so deep links and the browser Back/Forward buttons just work. The
// CrmSearchInput + IncludeArchivedToggle live on the parent panel
// alongside the sort dropdown; this bar owns the multi-select
// + range chips.

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  readProspectFiltersFromUrl,
  writeProspectFiltersToParams,
} from "./filterState";
import {
  FORM5500_STATUS_LABEL,
  LIVES_BAND_LABEL,
  MONTH_LABEL,
  SOURCE_LABEL,
  VALID_FORM5500_STATUS,
  VALID_LIVES_BANDS,
  VALID_SOURCES,
  type Form5500FilterStatus,
  type LivesBand,
  type ProspectFilters,
  type ProspectSource,
} from "./types";


export interface ProspectsFiltersBarProps {
  /** FE-SCRM-063 — count of currently-rendered rows. When the
   *  Form 5500 status facet is set, this surfaces alongside the
   *  active value (e.g. "Needs research (12)") so the producer
   *  sees the queue depth without leaving the filter bar. */
  total_rows?: number;
}


export function ProspectsFiltersBar(
  { total_rows }: ProspectsFiltersBarProps = {},
): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const filters = useMemo(
    () => readProspectFiltersFromUrl(search), [search],
  );

  const commit = useCallback(
    (next: ProspectFilters) => {
      const params = writeProspectFiltersToParams(next, search);
      params.delete("page");
      setSearch(params, { replace: false });
    },
    [search, setSearch],
  );

  function toggleSource(s: ProspectSource) {
    const set = new Set(filters.sources);
    if (set.has(s)) set.delete(s); else set.add(s);
    commit({ ...filters, sources: Array.from(set) });
  }

  function toggleLivesBand(b: LivesBand) {
    const set = new Set(filters.lives_bands);
    if (set.has(b)) set.delete(b); else set.add(b);
    commit({ ...filters, lives_bands: Array.from(set) });
  }

  function setRange(
    key: keyof Pick<
      ProspectFilters,
      "renewal_month_min" | "renewal_month_max" | "fit_score_min" | "fit_score_max"
    >,
    raw: string,
  ) {
    const value = raw === "" ? null : Number.parseInt(raw, 10);
    commit({ ...filters, [key]: Number.isFinite(value as number) ? value : null });
  }

  const has_any_filter = (
    filters.sources.length > 0
    || filters.lives_bands.length > 0
    || filters.renewal_month_min !== null
    || filters.renewal_month_max !== null
    || filters.fit_score_min !== null
    || filters.fit_score_max !== null
    || filters.form5500_status !== null
  );

  function setForm5500Status(next: Form5500FilterStatus | null) {
    commit({ ...filters, form5500_status: next });
  }

  return (
    <section
      data-testid="prospects-filters-bar"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs"
    >
      <FilterGroup label="Source">
        {VALID_SOURCES.map((s) => (
          <Chip
            key={s}
            active={filters.sources.includes(s)}
            onClick={() => toggleSource(s)}
          >
            {SOURCE_LABEL[s]}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Lives band">
        {VALID_LIVES_BANDS.map((b) => (
          <Chip
            key={b}
            active={filters.lives_bands.includes(b)}
            onClick={() => toggleLivesBand(b)}
          >
            {LIVES_BAND_LABEL[b]}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Renewal month">
        <RangeSelect
          ariaLabel="Renewal month from"
          value={filters.renewal_month_min}
          options={Array.from({ length: 12 }, (_, idx) => idx + 1)}
          labelFor={(m) => MONTH_LABEL[m]}
          onChange={(v) => setRange("renewal_month_min", v)}
        />
        <span className="text-text-tertiary">→</span>
        <RangeSelect
          ariaLabel="Renewal month to"
          value={filters.renewal_month_max}
          options={Array.from({ length: 12 }, (_, idx) => idx + 1)}
          labelFor={(m) => MONTH_LABEL[m]}
          onChange={(v) => setRange("renewal_month_max", v)}
        />
      </FilterGroup>

      <FilterGroup label="Fit score">
        <RangeInput
          ariaLabel="Fit score min"
          value={filters.fit_score_min}
          placeholder="min"
          onChange={(v) => setRange("fit_score_min", v)}
        />
        <span className="text-text-tertiary">→</span>
        <RangeInput
          ariaLabel="Fit score max"
          value={filters.fit_score_max}
          placeholder="max"
          onChange={(v) => setRange("fit_score_max", v)}
        />
      </FilterGroup>

      {/* FE-SCRM-063 — single-select Form 5500 link-state facet.
          The `Needs research` option is the surfacing of DATA-SCRM-017's
          research queue (sponsor IS NULL AND attempted_at IS NOT NULL). */}
      <FilterGroup label="Form 5500">
        <Form5500StatusFacet
          value={filters.form5500_status}
          onChange={setForm5500Status}
          count_when_set={
            filters.form5500_status !== null && total_rows !== undefined
              ? total_rows
              : null
          }
        />
      </FilterGroup>

      {has_any_filter && (
        <button
          type="button"
          onClick={() =>
            commit({
              ...filters,
              sources: [], lives_bands: [],
              renewal_month_min: null, renewal_month_max: null,
              fit_score_min: null, fit_score_max: null,
              form5500_status: null,
            })
          }
          className="ml-auto text-text-tertiary underline-offset-2 hover:underline"
        >
          Clear filters
        </button>
      )}
    </section>
  );
}


function Form5500StatusFacet({
  value, onChange, count_when_set,
}: {
  value: Form5500FilterStatus | null;
  onChange: (next: Form5500FilterStatus | null) => void;
  count_when_set: number | null;
}): JSX.Element {
  // Single-select dropdown chip — value=null is the "no filter" state.
  // When set, an inline × clears the facet without opening the menu and
  // the count-of-currently-rendered-rows surfaces alongside.
  return (
    <div className="flex items-center gap-1">
      <select
        data-testid="prospects-form5500-status-facet"
        aria-label="Form 5500 status"
        value={value ?? ""}
        onChange={(e) => {
          const next = e.target.value;
          onChange((next === "" ? null : next) as
            | Form5500FilterStatus | null);
        }}
        className={[
          "rounded-md border bg-white px-1.5 py-0.5 text-[11px]",
          value === null
            ? "border-gray-300 text-text-secondary"
            : "border-teal text-teal-strong font-medium",
        ].join(" ")}
      >
        <option value="">Any</option>
        {VALID_FORM5500_STATUS.map((s) => (
          <option key={s} value={s}>{FORM5500_STATUS_LABEL[s]}</option>
        ))}
      </select>
      {value !== null && count_when_set !== null && (
        <span
          data-testid="prospects-form5500-status-count"
          className={[
            "rounded-full bg-teal-soft px-1.5 py-0.5 text-[10px]",
            "font-medium tabular-nums text-teal-strong",
          ].join(" ")}
        >
          {count_when_set}
        </span>
      )}
      {value !== null && (
        <button
          type="button"
          aria-label="Clear Form 5500 filter"
          data-testid="prospects-form5500-status-clear"
          onClick={() => onChange(null)}
          className={[
            "rounded-full border border-gray-200 bg-white",
            "px-1.5 text-[11px] leading-none text-text-tertiary",
            "hover:text-text-primary",
          ].join(" ")}
        >
          ×
        </button>
      )}
    </div>
  );
}


function FilterGroup({
  label, children,
}: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-text-tertiary">{label}:</span>
      {children}
    </div>
  );
}


function Chip({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-2 py-0.5 text-[11px] transition-colors",
        active
          ? "border-teal bg-teal text-text-inverse"
          : "border-gray-200 bg-white text-text-secondary hover:border-gray-300",
      ].join(" ")}
    >
      {children}
    </button>
  );
}


function RangeSelect({
  ariaLabel, value, options, labelFor, onChange,
}: {
  ariaLabel: string;
  value: number | null;
  options: number[];
  labelFor: (n: number) => string;
  onChange: (raw: string) => void;
}): JSX.Element {
  return (
    <select
      aria-label={ariaLabel}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-[11px]"
    >
      <option value="">Any</option>
      {options.map((n) => (
        <option key={n} value={n}>{labelFor(n)}</option>
      ))}
    </select>
  );
}


function RangeInput({
  ariaLabel, value, placeholder, onChange,
}: {
  ariaLabel: string;
  value: number | null;
  placeholder: string;
  onChange: (raw: string) => void;
}): JSX.Element {
  return (
    <input
      type="number"
      min={0}
      max={100}
      aria-label={ariaLabel}
      value={value === null ? "" : String(value)}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-14 rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-[11px]"
    />
  );
}
